import { dismissBackgroundTask } from "@/store/background-tasks/background-tasks-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { ProcessingTask } from "@/utils/background-tasks";
import { NoTranslate, RemainingTimeLabel } from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  isBackgroundTaskActive,
  ProgressApiSupportedTaskTypes,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { CardError } from "../common/card-error";
import { CardProgress } from "../common/card-progress";
import { CardAction, GenericCardLayout } from "../layouts/generic-card-layout";
import {
  getTaskStatusLabel,
  getTaskTypeDescription,
} from "../task-descriptions";

type ProcessingTaskCardProps = {
  /** The task to track */
  task: ProcessingTask;
};

/** @returns the upload menu card to track a backend processing of a PointCloud or CAD/Bim */
export function ProcessingTaskCard({
  task,
}: ProcessingTaskCardProps): JSX.Element {
  const dispatch = useAppDispatch();
  const elementName = useElementName(task.iElementId);

  const name = useMemo(() => {
    if (elementName) {
      return <NoTranslate>{elementName}</NoTranslate>;
    }

    // Use custom name for tasks that do not have a reference id
    if (task.type === ProgressApiSupportedTaskTypes.sceneConversion) {
      return "SCENE conversion";
    }
    if (task.type === ProgressApiSupportedTaskTypes.pointCloudLazToPotree) {
      return "Scan processing";
    }
    if (task.type === ProgressApiSupportedTaskTypes.vlmIndexMigration) {
      return "AI data analysis";
    }

    return "Unknown";
  }, [elementName, task.type]);

  const subText = useMemo(() => getTaskTypeDescription(task), [task]);

  const dismissErrorTask = useCallback((): void => {
    dispatch(dismissBackgroundTask(task.id));
  }, [dispatch, task.id]);

  const menu = useMemo<CardAction[]>(() => {
    if (
      task.state === BackgroundTaskState.failed &&
      task.type !== ProgressApiSupportedTaskTypes.sceneConversion
    ) {
      return [
        {
          name: "Clear",
          callback: dismissErrorTask,
        },
      ];
    }
    return [];
  }, [dismissErrorTask, task.state, task.type]);
  const errorHint =
    task.type === ProgressApiSupportedTaskTypes.sceneConversion
      ? "Save again the project in SCENE and press the Sync button to start the processing."
      : undefined;

  return (
    <GenericCardLayout
      name={name}
      subText={subText}
      menu={menu}
      startTime={task.createdAt}
    >
      {isBackgroundTaskActive(task.state) && (
        <CardProgress
          label={getTaskStatusLabel(task)}
          subLabel={
            task.expectedEnd && (
              <NoTranslate>
                <RemainingTimeLabel expectedEnd={task.expectedEnd} />
              </NoTranslate>
            )
          }
          task={task}
        />
      )}
      {task.state === BackgroundTaskState.failed && (
        <CardError
          task={task}
          hint={errorHint}
          showDetails={
            task.type === ProgressApiSupportedTaskTypes.sceneConversion
          }
        />
      )}
    </GenericCardLayout>
  );
}

/**
 * @returns The name of an iElement. If the iElement is not yet in the store,
 * it's fetched from the ProjectAPI
 * @param id The id of the iElement
 */
function useElementName(id: GUID | null): string | undefined {
  const { projectApiClient } = useApiClientContext();
  const [name, setName] = useState<string>();

  const element = useAppSelector(selectIElement(id));
  useEffect(() => {
    async function fetchName(): Promise<string | undefined> {
      if (!id) return;

      const elements = await projectApiClient.getAllIElements({
        ids: [id],
      });

      setName(elements.at(0)?.name);
    }

    if (element) {
      setName(element.name);
      return;
    }

    fetchName();
  }, [element, id, projectApiClient]);

  return name;
}
